import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { NebulaConversationDetail } from '../../../../types/nebula-insights';
import { formatDateTime, formatRelativeTime, formatUserName } from '../utils';
import ConversationPdfExport from './ConversationPdfExport';
import MessageThread from './MessageThread';

interface ConversationDetailDrawerProps {
  open: boolean;
  loading: boolean;
  conversation: NebulaConversationDetail | null;
  onClose: () => void;
}

const ConversationDetailDrawer: React.FC<ConversationDetailDrawerProps> = ({
  open,
  loading,
  conversation,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close conversation drawer overlay"
        className="fixed inset-0 bg-black/40 z-[10000]"
        onClick={onClose}
      />
      <aside
        className="fixed top-0 right-0 h-full w-full max-w-xl bg-white border-l border-gray-200 z-[10001] flex flex-col shadow-2xl"
        aria-hidden={!open}
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="min-w-0">
            {conversation && (
              <>
                <p className="text-xs text-gray-500 font-family-messina-sans mb-1">
                  {conversation.artistName || 'Unnamed artist'}
                </p>
                <h2 className="text-lg font-medium text-gray-900 font-family-messina-modern truncate">
                  {conversation.title || 'Untitled conversation'}
                </h2>
              </>
            )}
            {!conversation && !loading && (
              <h2 className="text-lg font-medium text-gray-900 font-family-messina-modern">
                Conversation
              </h2>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {conversation && !loading && (
              <ConversationPdfExport
                messages={conversation.messages}
                conversationTitle={conversation.title || 'Untitled conversation'}
              />
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {conversation && (
          <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap gap-2 shrink-0">
            <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-xs text-gray-600 font-family-messina-sans">
              {conversation.messageCount} messages
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-xs text-gray-600 font-family-messina-sans">
              Created {formatDateTime(conversation.createdAt)}
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-xs text-gray-600 font-family-messina-sans">
              Last active {formatRelativeTime(conversation.lastActiveAt ?? conversation.updatedAt)}
            </span>
            {conversation.model && (
              <span className="px-2.5 py-1 rounded-lg bg-gray-900 text-xs text-white font-family-messina-sans">
                {conversation.model}
              </span>
            )}
            {conversation.user && (
              <span className="px-2.5 py-1 rounded-lg bg-gray-100 text-xs text-gray-600 font-family-messina-sans">
                {formatUserName(conversation.user.firstname, conversation.user.lastname)}
                {' · '}
                {conversation.user.email}
              </span>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
            </div>
          ) : conversation ? (
            <MessageThread messages={conversation.messages} />
          ) : (
            <p className="text-sm text-gray-500 font-family-messina-sans text-center py-8">
              Conversation not found
            </p>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default ConversationDetailDrawer;
